import { lazy } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router';
import { RootLayout } from './layouts/RootLayout.js';
import { AdminLayout } from './layouts/AdminLayout.js';
import { DEFAULT_ADMIN_TAB } from './layouts/adminTabs.js';
import { AuthCallback, SilentRenew } from './pages/AuthCallback.js';

// Lazy-loaded pages — each subagent's port lands at the path below.
const OrdersPage = lazy(() =>
  import('./pages/orders/OrdersPage.js').then((m) => ({ default: m.OrdersPage })),
);
const RevisionHistoryPage = lazy(() =>
  import('./pages/revision-history/RevisionHistoryPage.js').then((m) => ({
    default: m.RevisionHistoryPage,
  })),
);
const RevisionDetailPage = lazy(() =>
  import('./pages/revision-detail/RevisionDetailPage.js').then((m) => ({
    default: m.RevisionDetailPage,
  })),
);
const ValuationsPage = lazy(() =>
  import('./pages/valuations/ValuationsPage.js').then((m) => ({ default: m.ValuationsPage })),
);
const SettlementSheetPage = lazy(() =>
  import('./pages/settlement-sheet/SettlementSheetPage.js').then((m) => ({
    default: m.SettlementSheetPage,
  })),
);
const SiteTimeCodesPage = lazy(() =>
  import('./pages/site-time-codes/SiteTimeCodesPage.js').then((m) => ({
    default: m.SiteTimeCodesPage,
  })),
);
const RatesPage = lazy(() =>
  import('./pages/rates/RatesPage.js').then((m) => ({ default: m.RatesPage })),
);

// Waves 1-3 pages
const FieldAdminPage = lazy(() =>
  import('./pages/field-admin/FieldAdminPage.js').then((m) => ({ default: m.FieldAdminPage })),
);
const IntegrationsPage = lazy(() =>
  import('./pages/integrations/IntegrationsPage.js').then((m) => ({
    default: m.IntegrationsPage,
  })),
);
const ImportHistoryPage = lazy(() =>
  import('./pages/import-history/ImportHistoryPage.js').then((m) => ({
    default: m.ImportHistoryPage,
  })),
);
const TriagePage = lazy(() =>
  import('./pages/triage/TriagePage.js').then((m) => ({ default: m.TriagePage })),
);

const DayworksLabourPage = lazy(() =>
  import('./pages/operations/DayworksLabourPage.js').then((m) => ({
    default: m.DayworksLabourPage,
  })),
);

const CostsPage = lazy(() =>
  import('./pages/operations/CostsPage.js').then((m) => ({
    default: m.CostsPage,
  })),
);

const SundryCategoryAdminPage = lazy(() =>
  import('./pages/sundries/SundryCategoryAdminPage.js').then((m) => ({
    default: m.SundryCategoryAdminPage,
  })),
);

const NotFoundPage = lazy(() =>
  import('./pages/NotFoundPage.js').then((m) => ({ default: m.NotFoundPage })),
);

// Admin-area pages live under the AdminLayout vertical-tabs shell at
// /admin/<tab> (see layouts/adminTabs.ts). The old top-level paths redirect
// there so pre-existing bookmarks keep working.
const ADMIN_REDIRECTS = [
  { from: 'site-time-codes', to: '/admin/site-time-codes' },
  { from: 'field-admin', to: '/admin/field-admin' },
  { from: 'sundry-categories', to: '/admin/sundry-categories' },
  { from: 'triage', to: '/admin/triage' },
  { from: 'integrations', to: '/admin/integrations' },
  { from: 'import-history', to: '/admin/import-history' },
  // The `/administration/*` forms are inherited from the retired Web.React app, whose
  // route table used that prefix. The Old Stack still links out to them from its
  // Administration menu (Web/Views/Administration/Index.cshtml) and users hold bookmarks,
  // so they resolve here rather than 404. Two are NOT a straight prefix swap: Web.React's
  // `valuation` is this app's top-level `/valuations`, and its `timesheet-management`
  // (SiteTimeCodeManagement) is `/admin/site-time-codes`.
  { from: 'administration/field-admin', to: '/admin/field-admin' },
  { from: 'administration/integrations', to: '/admin/integrations' },
  { from: 'administration/import-history', to: '/admin/import-history' },
  { from: 'administration/triage-order-mappings', to: '/admin/triage' },
  { from: 'administration/timesheet-management', to: '/admin/site-time-codes' },
  { from: 'administration/valuation', to: '/valuations' },
];

export const routes: RouteObject[] = [
  // OIDC redirect targets — outside RootLayout (no sidebar during the handshake).
  { path: '/auth/callback', Component: AuthCallback },
  { path: '/auth/signout', Component: AuthCallback },
  { path: '/auth/silent-renew', Component: SilentRenew },
  {
    path: '/',
    Component: RootLayout,
    ErrorBoundary: NotFoundPage,
    children: [
      { index: true, element: <Navigate to="/orders" replace /> },
      { path: 'orders', Component: OrdersPage },
      // Legacy event-scoped order URLs, carried over from the AngularJS app and
      // from Web.React (AppRoutes.tsx:127,135). They are bookmarked and emailed,
      // so they must keep resolving: OrdersPage folds the `:eventId` param into
      // EventContext and opens the sidecar for `:orderId` (see useRouteEventSync).
      { path: 'events/:eventId/orders', Component: OrdersPage },
      { path: 'events/:eventId/orders/:orderId', Component: OrdersPage },
      { path: 'orders/:orderId/revisions', Component: RevisionHistoryPage },
      { path: 'orders/:orderId/revisions/:revisionId', Component: RevisionDetailPage },
      // Host 2: per-order bulk day-work labour grid (operations area; ?orderId=).
      { path: 'operations/dayworks', Component: DayworksLabourPage },
      // Per-order material/plant-hire/bought-in services grid (operations area; ?orderId=).
      { path: 'operations/costs', Component: CostsPage },
      { path: 'valuations', Component: ValuationsPage },
      { path: 'settlement-sheets/:sheetId?', Component: SettlementSheetPage },
      { path: 'rates', Component: RatesPage },
      {
        path: 'admin',
        Component: AdminLayout,
        children: [
          { index: true, element: <Navigate to={DEFAULT_ADMIN_TAB.href} replace /> },
          { path: 'site-time-codes', Component: SiteTimeCodesPage },
          { path: 'field-admin', Component: FieldAdminPage },
          { path: 'sundry-categories', Component: SundryCategoryAdminPage },
          { path: 'triage', Component: TriagePage },
          { path: 'integrations', Component: IntegrationsPage },
          { path: 'import-history', Component: ImportHistoryPage },
        ],
      },
      ...ADMIN_REDIRECTS.map(({ from, to }) => ({
        path: from,
        element: <Navigate to={to} replace />,
      })),
      // Catch-all. Sits inside RootLayout so an unknown URL keeps the shell
      // (sidebar + topbar) and the user can navigate on from it. The same
      // component doubles as the route ErrorBoundary above.
      { path: '*', Component: NotFoundPage },
    ],
  },
];

export const router = createBrowserRouter(routes);
